import PropTypes from 'prop-types';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import {
  Brush,
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  YAxis,
  XAxis
} from 'recharts';
import Button from 'react-bootstrap/Button';
import ChartContext from './DataEntrySection/DataEntryProvider';
import { getSigDigitsUtil, formatChartPointDateTime } from '../util/util';
import SeriesPickerModal from './Modal/SeriesPickerModal';
import { buildSeriesLocations } from '../util/seriesPicker';
import { getLaboratoryOnly } from '../util/locationLabel';

const uniq = require('lodash/uniq');

function SQCInstrumentsChart({
  instrumentsArray,
  instrumentsDots,
  data,
  syncId,
  xAxis,
  yMin,
  yMax,
  lineType,
  maxUCL,
  minLCL,
  hideRegionArea,
  showDateTimeInTooltip,
  tz,
  dateTimeFormat
}) {
  const [instruments, setInstruments] = useState([]);
  const [currentInstrumentsArray, setCurrentInstrumentsArray] = useState(instrumentsArray);
  const { currentControlStrategyConfigurations } = useContext(ChartContext);
  const [instrumentData] = useState(data);
  const [showPicker, setShowPicker] = useState(false);

  const formatNothing = () => {
    return '';
  };

  const formatXAxis = (tickItem) => {
    return String(data[tickItem]?.date) || '';
  };

  const renderSigDigits = (stringValue) => {
    return getSigDigitsUtil(stringValue, currentControlStrategyConfigurations.chartSigDigits);
  };

  const formatYAxis = (tickItem) => {
    return renderSigDigits(tickItem);
  };

  const CustomizedDotInstrument = ({ cx, cy, payload }) => {
    const dotToReturn = [];
    if (cy !== null) {
      const currentInstrument = instrumentsDots?.find(
        (instrument) => instrument?.name === payload?.instrument
      );
      const currentIndex = currentInstrumentsArray?.findIndex(
        (instrument) => instrument?.name === currentInstrument?.name
      );
      if (currentInstrumentsArray[currentIndex]?.checked) {
        dotToReturn.push(
          <svg
            x={cx - 3}
            y={cy - 3}
            width={15}
            height={15}
            key={uuidv4()}
            fill={currentInstrument.color}
            viewBox="0 0 1024 1024"
          >
            <path d="M512 256C512 397.4 397.4 512 256 512C114.6 512 0 397.4 0 256C0 114.6 114.6 0 256 0C397.4 0 512 114.6 512 256z" />
          </svg>
        );
      }
    }
    return dotToReturn;
  };

  // Shared by single toggles and Select all / Clear: flip the checked flag on the named
  // instruments, then keep the rendered lines (`instruments`) in sync with the flags.
  const setInstrumentsChecked = (names, checked) => {
    const nameSet = new Set(names);
    const updatedInstrumentsArray = currentInstrumentsArray.map((instrument) =>
      nameSet.has(instrument.name) ? { ...instrument, checked } : instrument
    );
    setCurrentInstrumentsArray(updatedInstrumentsArray);
    // Keep lines in the stable instrumentsArray order so bulk toggles don't reshuffle them.
    const visible = new Set(instruments);
    names.forEach((name) => (checked ? visible.add(name) : visible.delete(name)));
    setInstruments(
      updatedInstrumentsArray
        .filter((instrument) => visible.has(instrument.name))
        .map((instrument) => instrument.name)
    );
  };

  const handleCheckboxClick = (name, checked) => setInstrumentsChecked([name], checked);

  const selectedCount = currentInstrumentsArray.filter((instrument) => instrument.checked).length;

  // The comparison query spans every location for this method/sample/parameter, so tag
  // each instrument with the location(s) its points come from.
  const instrumentLocations = useMemo(
    () => buildSeriesLocations(data, 'instrument', hideRegionArea ? getLaboratoryOnly : undefined),
    [data, hideRegionArea]
  );
  const pickerItems = currentInstrumentsArray.map((instrument) => ({
    ...instrument,
    locations: instrumentLocations.get(instrument.name) || []
  }));

  useEffect(() => {
    if (Array.isArray(instrumentData)) {
      let currentInstruments = [];
      let sortedInstrumentChartData;
      if (data) {
        sortedInstrumentChartData = data;
      } else {
        sortedInstrumentChartData = instrumentData;
      }

      for (const dataPoint of sortedInstrumentChartData) {
        dataPoint[dataPoint.instrument] = dataPoint.result;
        currentInstruments.push(dataPoint.instrument);
      }
      currentInstruments = uniq(currentInstruments);
      setInstruments(currentInstruments);
    }
  }, [instrumentData]);

  const renderInstrumentLines = (currentInstruments) => {
    const linesToReturn = [];
    const updatedInstruments = currentInstruments;
    updatedInstruments.forEach((instrument) => {
      const currentInstrument = currentInstrumentsArray.find(
        (instrumentToFind) => instrument === instrumentToFind.name
      );
      const currentColor = currentInstrument.color;
      linesToReturn.push(
        <Line
          connectNulls
          name={instrument}
          type={lineType}
          dataKey={instrument}
          stroke={currentColor}
          fill={currentColor}
          isAnimationActive={false}
          dot={<CustomizedDotInstrument key={uuidv4()} instrumentsDots={instrumentsDots} />}
        />
      );
    });
    return linesToReturn;
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const description = `${payload[0].dataKey}: ${payload[0].value}`;
      // Opt-in: show date + time like the results table; otherwise keep the date-only line.
      const dateTime = showDateTimeInTooltip
        ? formatChartPointDateTime(payload[0].payload, tz, dateTimeFormat) ||
          `${payload[0].payload.date}`
        : `${payload[0].payload.date}`;
      const color = `${payload[0].color}`;
      return (
        <div className="custom-tooltip">
          <p>{label}</p>
          <p
            style={{
              color: color
            }}
          >
            {description}
          </p>
          <p>{dateTime}</p>
        </div>
      );
    }

    return null;
  };

  return (
    <>
      <div className="series-picker-toggle">
        <Button variant="outline-secondary" size="sm" onClick={() => setShowPicker(true)}>
          {`Instruments (${selectedCount} of ${currentInstrumentsArray.length}) \u25BE`}
        </Button>
      </div>
      <SeriesPickerModal
        show={showPicker}
        onHide={() => setShowPicker(false)}
        title="Instruments"
        searchPlaceholder="Search instruments or locations"
        idPrefix="instrument-picker"
        items={pickerItems}
        onToggle={handleCheckboxClick}
        onSetChecked={setInstrumentsChecked}
      />
      <ResponsiveContainer key={uuidv4()} height={400}>
        <LineChart
          data={instrumentData}
          key={uuidv4()}
          syncId={syncId}
          margin={{
            top: 5,
            right: 20,
            bottom: 15,
            left: 20
          }}
        >
          {renderInstrumentLines(instruments)}
          {maxUCL !== undefined && maxUCL !== null && (
            <ReferenceLine
              y={maxUCL}
              stroke="#f56f42"
              strokeDasharray="5 5"
              label={{ value: `UCL: ${renderSigDigits(maxUCL)}`, position: 'center' }}
            />
          )}
          {minLCL !== undefined && minLCL !== null && (
            <ReferenceLine
              y={minLCL}
              stroke="#f56f42"
              strokeDasharray="5 5"
              label={{ value: `LCL: ${renderSigDigits(minLCL)}`, position: 'center' }}
            />
          )}
          <CartesianGrid stroke="#ccc" key={uuidv4()} strokeDasharray="5 5" />
          <XAxis tickFormatter={formatXAxis} />
          <YAxis type="number" key={uuidv4()} domain={[yMin, yMax]} tickFormatter={formatYAxis} />
          <Brush dataKey={xAxis} key={uuidv4()} tickFormatter={formatNothing} />
          <Tooltip content={<CustomTooltip />} />
        </LineChart>
      </ResponsiveContainer>
    </>
  );
}

SQCInstrumentsChart.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  syncId: PropTypes.string.isRequired,
  xAxis: PropTypes.string.isRequired,
  yMin: PropTypes.number.isRequired,
  yMax: PropTypes.number.isRequired,
  instrumentsArray: PropTypes.array.isRequired,
  instrumentsDots: PropTypes.array.isRequired,
  lineType: PropTypes.string,
  maxUCL: PropTypes.number,
  minLCL: PropTypes.number,
  hideRegionArea: PropTypes.bool,
  showDateTimeInTooltip: PropTypes.bool,
  tz: PropTypes.string,
  dateTimeFormat: PropTypes.string
};

SQCInstrumentsChart.defaultProps = {
  lineType: 'monotone',
  maxUCL: undefined,
  minLCL: undefined,
  hideRegionArea: false,
  showDateTimeInTooltip: false
};

export default SQCInstrumentsChart;
